import * as Sentry from '@sentry/react'
import { PresentationManifest } from '@sketch-hq/sketch-web-renderer'
import {
  PrototypeFrame,
  FrameToPageMap,
  FrameDataMap,
} from './ContentsTab.types'

/**
 * Fetches a presentation manifest from a URL.
 */
export const fetchManifestFromUrl = async (
  manifestUrl: string
): Promise<PresentationManifest> => {
  const response = await fetch(manifestUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch manifest: ${response.statusText}`)
  }
  return await response.json()
}

/**
 * Builds lookup maps for efficient frame and page access from manifest data.
 */
export const buildFrameMaps = (manifest: PresentationManifest) => {
  const frameToPageMap: FrameToPageMap = new Map()
  const frameDataMap: FrameDataMap = new Map()

  if (!manifest?.contents?.pages) {
    return { frameToPageMap, frameDataMap }
  }

  for (const page of manifest.contents.pages) {
    if (page.frames) {
      for (const frame of page.frames) {
        frameToPageMap.set(frame.id, page)
        frameDataMap.set(frame.id, frame)
      }
    }
  }

  return { frameToPageMap, frameDataMap }
}

/**
 * Traverses a prototype's flow graph using breadth-first search to find all connected pages.
 */
export const traversePrototypeFlow = (
  prototypeId: string,
  frameToPageMap: FrameToPageMap,
  frameDataMap: FrameDataMap
): string[] => {
  if (!frameToPageMap.has(prototypeId)) {
    return []
  }

  const pageUuids = new Set<string>()
  const visitedFrames = new Set<string>()
  const framesToVisit = [prototypeId]

  while (framesToVisit.length > 0) {
    const currentFrameId = framesToVisit.shift()!

    if (visitedFrames.has(currentFrameId)) {
      continue
    }
    visitedFrames.add(currentFrameId)

    // Add the current frame's page to the result set
    const currentPage = frameToPageMap.get(currentFrameId)
    if (currentPage?.id) {
      pageUuids.add(currentPage.id)
    }

    // Queue all destination frames from this frame's flows
    const frameData = frameDataMap.get(currentFrameId)
    if (frameData?.flows) {
      for (const flow of frameData.flows) {
        if (flow.destinationId && !visitedFrames.has(flow.destinationId)) {
          framesToVisit.push(flow.destinationId)
        }
      }
    }
  }

  return Array.from(pageUuids)
}

/**
 * Determines all pages that are part of a prototype's flow.
 * Uses a cached manifest if available, otherwise fetches it on-demand.
 */
export const getPrototypePageUuids = async (
  prototypeId: string,
  fullPrototypes: PrototypeFrame[],
  manifest: PresentationManifest | null
): Promise<string[]> => {
  const prototype = fullPrototypes.find(p => p.uuid === prototypeId)
  const fallbackPageUuid = prototype?.page?.uuid

  if (!manifest?.contents?.pages) {
    return fallbackPageUuid ? [fallbackPageUuid] : []
  }

  // Build lookup maps and traverse the flow graph
  const { frameToPageMap, frameDataMap } = buildFrameMaps(manifest)
  const pageUuids = traversePrototypeFlow(
    prototypeId,
    frameToPageMap,
    frameDataMap
  )

  return pageUuids.length > 0
    ? pageUuids
    : fallbackPageUuid
      ? [fallbackPageUuid]
      : []
}

/**
 * Fetches the presentation manifest from the first available prototype.
 */
export const fetchInitialManifest = async (
  fullPrototypes: PrototypeFrame[]
): Promise<PresentationManifest | null> => {
  if (fullPrototypes.length === 0) {
    return null
  }

  const prototypeWithManifest = fullPrototypes.find(
    p => p?.page?.presentationFile?.manifestUrl
  )

  if (!prototypeWithManifest?.page?.presentationFile?.manifestUrl) {
    return null
  }

  try {
    return await fetchManifestFromUrl(
      prototypeWithManifest.page.presentationFile.manifestUrl
    )
  } catch (error) {
    Sentry.captureException(error)
    return null
  }
}
